import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import { paragraphLarge, paragraphMedium, paragraphSmall } from '@helpers/typography';
import Image from '@stories/Components/Misc/Image/Image';
import styled from 'styled-components';
const Container = styled.section `
  --shapeTransform: translateX(${fluid(78, 122)}) translateY(calc(-1 * ${fluid(132, 218)}));
  --borderRadius: 10px;

  height: 100%;
  background: ${brand.white};
  border-radius: var(--borderRadius);
  color: ${brand.base.blue};
  position: relative;

  &[data-alt='true'] {
    --shapeTransform: translateX(calc(-1 * ${fluid(78, 122)})) translateY(${fluid(132, 218)});
  }

  &[data-variant='carousel'] {
    --avatarSize: ${fluid(58, 88)};
    --avatarBorder: 2px;
  }

  &[data-variant='single'] {
    --avatarSize: ${fluid(124, 272)};
    --avatarBorder: ${fluid(12, 24)};
  }

  @media ${until(Device.Tablet)} {
    padding: ${fluid(62, 76)} ${fluid(16, 104)};

    &[data-variant='single'] {
      padding-top: calc(var(--avatarSize) * 1.25);
      margin-top: calc(var(--avatarSize) * 0.5);
    }
  }

  @media ${from(Device.Tablet)} {
    &[data-variant='carousel'] {
      padding: ${fluid(16, 48)} ${fluid(68, 104)};
    }

    &[data-variant='single'] {
      margin-left: calc(var(--avatarSize) * 0.5);
      padding: ${fluid(48, 114)} ${fluid(64, 148)} ${fluid(48, 114)} var(--avatarSize);
    }
  }
`;
const ShapeWrapper = styled.div `
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  overflow: hidden;
  border-radius: var(--borderRadius);

  &::before {
    ${transition('transform', 1)};

    content: '';
    position: absolute;
    background: rgba(0, 43, 71, 0.03);
    border-radius: 50%;
    height: ${fluid(288, 493)};
    width: ${fluid(318, 544)};
    transform: var(--shapeTransform);
    transition-delay: 1s;

    ${Container}[data-alt='false'] & {
      top: 0;
      right: 0;
    }

    ${Container}[data-alt='true'] & {
      bottom: 0;
      left: 0;
    }

    [data-animate='false'] & {
      transform: var(--shapeTransform) scale(0);
    }
  }
`;
const Inner = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  z-index: 0;
`;
const Content = styled.div `
  ${transition('transform, opacity')};
  ${paragraphLarge}

  transition-delay: 500ms;
  margin: 0 0 ${fluid(32, 64)};

  [data-animate='false'] & {
    transform: translateY(50%);
    opacity: 0;
  }
`;
const Citation = styled.div `
  display: flex;
  align-items: center;
  margin-top: auto;
`;
const AvatarWrapper = styled.div `
  ${transition('opacity', 1.5)};

  border: var(--avatarBorder) solid ${brand.primary.blue};
  border-radius: 50%;
  flex: 0 0 auto;
  height: var(--avatarSize);
  margin-right: ${fluid(16, 24)};
  overflow: hidden;
  width: var(--avatarSize);
  transition-delay: 300ms;

  ${Container}[data-variant='single'] & {
    position: absolute;
    top: 0;
    transform: translateY(-175%);

    @media ${from(Device.Tablet)} {
      top: 50%;
      left: calc(var(--avatarSize) * -1);
      transform: translate(-50%, -50%);
    }
  }

  [data-animate='false'] & {
    opacity: 0;
  }
`;
const Avatar = styled(Image) `
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;
`;
const AvatarPlaceholder = styled.span `
  display: flex;
  color: ${brand.primary.blue};
  font-size: ${fluid(24, 32)};
  height: 100%;
  line-height: 1em;
  opacity: 0.5;
  padding-top: 4px;
  place-content: center;
  place-items: center;
  width: 100%;
`;
const NameWrapper = styled.div `
  ${transition('transform, opacity')};

  transition-delay: 500ms;

  [data-animate='false'] & {
    transform: translateY(50%);
    opacity: 0;
  }
`;
const Name = styled.div `
  ${paragraphMedium}

  margin: 0;
`;
const Title = styled.div `
  ${paragraphSmall}

  margin: 0;
`;
const QuoteIcon = styled.span `
  ${transition('transform, opacity')};

  --spacing: ${fluid(16, 24)};

  position: absolute;
  display: block;
  filter: drop-shadow(4px 4px 0px ${brand.primary.blueGrey});
  height: ${fluid(38, 52)};
  color: ${brand.primary.orange};
  width: ${fluid(35, 56)};
  transition-delay: 900ms;

  &[data-open] {
    top: var(--spacing);
    left: var(--spacing);

    ${Container}[data-variant='single'] & {
      @media ${until(Device.Tablet)} {
        top: calc(var(--avatarSize) * 0.75);
      }

      @media ${from(Device.Tablet)} {
        left: calc(var(--avatarSize) * 1 - var(--spacing));
      }
    }

    [data-animate='false'] & {
      transform: translateY(-100%);
      opacity: 0;
    }
  }

  &[data-close] {
    bottom: var(--spacing);
    right: var(--spacing);

    [data-animate='false'] & {
      transform: translateY(100%);
      opacity: 0;
    }
  }

  @media ${from(Device.Tablet)} {
    ${Container}[data-variant='single'] & {
      --spacing: ${fluid(48, 84)};
    }
  }
`;
const ReviewCardStyles = {
    Avatar,
    AvatarPlaceholder,
    AvatarWrapper,
    Citation,
    Container,
    Content,
    Inner,
    Name,
    NameWrapper,
    QuoteIcon,
    Title,
    ShapeWrapper,
};
export default ReviewCardStyles;
